import { domToReact } from "html-react-parser";
import React from "react";
import { renderToString } from "react-dom/server";
import { isMobile } from "../bridges/BridgeServices";
import BannerImage from "../components/BannerImage/bannerImage";
import CollapsibleContent from "../components/CollapsibleContent/CollapsibleContent";
import CustomTable from "../components/CustomTable/CustomTable";
import Embed from "../components/Embed/Embed";
import GlossaryItem from "../components/GlossaryItem/GlossaryItem";
import Stepper from "../components/Stepper/Stepper";
import Video from "../components/Video/Video";
import { createRedirectURL } from "./helperFunctions";
import { CUSTOM_BLOCK_CLASSNAMES } from "./nodeHelper";

const {
  CLASSES: {
    COLLAPSIBLE_SECTION,
    BANNER_IMAGE,
    STEPPER,
    CUSTOM_TABLE,
    GLOSSARY,
    VIDEO,
  },
  GLOSSARY_EXCLUDED_ROUTES,
} = require("./constants");

const contentParserOptions = {
  replace: domNode => {
    // add data-blocktype as custom-block only if the content is not part of FAQ Content
    if (
      domNode?.attribs?.["data-blocktype"] ===
      "custom-block"
    ) {
      return <></>;
    }
    // add class names if the content is part of FAQ Content

    if (domNode && domNode?.attribs?.class) {
      return CLASS_TO_PARSER_MAP[domNode?.attribs?.class]?.(
        domNode
      );
    }

    if (
      domNode &&
      domNode.name === "a" &&
      domNode.attribs &&
      domNode.attribs.href
    ) {
      if (domNode.attribs.href[0] === "/") {
        domNode.attribs.href = process.env
          .GATSBY_PATH_PREFIX
          ? "/" +
            process.env.GATSBY_PATH_PREFIX +
            createRedirectURL(domNode.attribs.href)
          : createRedirectURL(domNode.attribs.href);
      }
    }
  },
};

export const collapsibleContentParser = domNode => {
  const content = [];
  let heading = "";
  if (domNode?.children) {
    heading =
      domNode?.children?.[0]?.children?.[0]?.children?.[0]
        ?.data;
    content?.push(
      renderToString(
        domToReact(domNode?.children?.[1]?.children)
      )
    );
  }
  let domNodeTemp = domNode?.next;

  while (domNodeTemp) {
    if (domNodeTemp?.data === "\n\n") {
      domNodeTemp = domNodeTemp?.next;
    } else {
      domNodeTemp = null;
    }
  }
  return (
    <CollapsibleContent
      heading={heading}
      content={content}
    />
  );
};

export const bannerContentParser = domNode => {
  let bannerImgUrl = "";
  try {
    const htmlContent =
      domNode.children[2].children[3].children[0]
        .children[1];

    bannerImgUrl = htmlContent.attribs.srcset
      ?.split(",")[5]
      ?.split(" ")?.[0];

    return <BannerImage bannerImgUrl={bannerImgUrl} />;
  } catch {
    console.log("bannerImgUrl not provided correctly");
    return <></>;
  }
};

export const stepperContentParser = domNode => {
  const contentList = [];
  let content = domNode?.children?.[1].children[0];
  while (content) {
    if (content && content?.children?.length > 0) {
      const htmlContent = renderToString(
        domToReact(content?.children)
      );
      contentList?.push(htmlContent);
      content = content?.next;
    } else if (
      content?.data === "\n\n" ||
      content?.data === "\n\n\n\n"
    ) {
      content = content?.next;
    } else {
      content = null;
    }
  }
  return <Stepper contentList={contentList} />;
};

export const customTableContentParser = domNode => {
  try {
    let domNodeTemp = domNode;
    const tableDom =
      domNodeTemp?.children?.[1]?.children?.[1];
    const tableType =
      domNodeTemp?.children?.[3]?.children?.[0]?.data;

    if (tableType === CUSTOM_TABLE.TABLE_TYPE.HORIZONTAL) {
      tableDom.children[0].children[0].children[0].children.forEach(
        child =>
          (child.attribs.class = CUSTOM_TABLE.BACKGROUND)
      );
    } else if (
      tableType === CUSTOM_TABLE.TABLE_TYPE.VERTICAL
    ) {
      tableDom.children[0].children[0].children.forEach(
        child =>
          (child.children[0].attribs.class =
            CUSTOM_TABLE.BACKGROUND)
      );
    }

    const tableComp = renderToString(
      domToReact(domNodeTemp.children[1].children)
    );

    return <CustomTable tableComp={tableComp} />;
  } catch (err) {
    console.log("Table values not provided correctly");
    return <></>;
  }
};

export const videoContentParser = domNode => {
  try {
    // We have to show same video src for web and mobile when only one video file is uploaded
    // Also if a file is not uploaded in WP we get the src as String 'No'

    let videoToRender = domNode?.children[1];
    if (
      !isMobile() &&
      domNode?.children?.[3]?.attribs?.src !== "No"
    ) {
      videoToRender = domNode?.children[3];
    }

    const videoString = renderToString(
      domToReact([videoToRender])
    );

    return <Video videoString={videoString} />;
  } catch {
    console.log("Video not uploaded correctly");
    return <></>;
  }
};

export const embedContentParser = domNode => {
  try {
    const URL = domNode?.attribs?.["data-url"];

    return <Embed URL={URL} />;
  } catch {
    console.log("Embeddable URL not provided correctly");
    return <></>;
  }
};

const glossaryParser = domNode => {
  try {
    let isGlossaryExcluded = GLOSSARY_EXCLUDED_ROUTES.some(
      a => window.location.pathname.includes(a)
    );

    if (isGlossaryExcluded)
      return <span>{domNode.children[0].data}</span>;

    let glossaryTitle = domNode.children[0].data;
    let glossaryDescription = renderToString(
      domToReact([domNode.children[1].children[2]])
    );

    return (
      <GlossaryItem
        title={glossaryTitle}
        description={glossaryDescription}
      />
    );
  } catch {
    console.log(
      "glossary not able to parse properly. Dom-Node for reference",
      domNode
    );
    return <></>;
  }
};
const CLASS_TO_PARSER_MAP = {
  [STEPPER.CONTAINER]: stepperContentParser,
  [COLLAPSIBLE_SECTION.SECTION]: collapsibleContentParser,
  [BANNER_IMAGE.SECTION]: bannerContentParser,
  [CUSTOM_BLOCK_CLASSNAMES.VIDEO_TAG]: videoContentParser,
  [CUSTOM_BLOCK_CLASSNAMES.EMBED]: embedContentParser,
  [CUSTOM_TABLE.CONTAINER]: customTableContentParser,
  [GLOSSARY.CONTAINER]: glossaryParser,
};

export default contentParserOptions;
